import {
  IAgentCertificateError,
  IAgentCertificateErrorExpire,
  hd,
  FeatureToggleService,
  PageTitleService,
  AuthorizationService,
  TicketService,
  GroupContextService,
  CertificateService,
  UserSettingsService,
  AuthenticationService,
  XDeviceService,
  IDashboardConfig,
  IGroupModel,
  ErrorHandlerService,
  UserContextService,
  AppSettingsService,
} from '@nx-fe/services';
import { Ticket } from './ticket';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { XsI18nPipe } from '@nx-fe/pipes';
import { Injectable } from '@angular/core';
import { TicketSearchService } from 'app/services/ticket-search.service';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import * as JSZIP from 'jszip';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';

type IError = hd.IError;
type IInfoGraphicFilterList = hd.IInfoGraphicFilterList;
type IDropdownListItem = hd.IDropdownListItem;

export interface TicketParams {
  searchText?: string;
  filter?: string;
  take?: string;
  skip?: string;
  group?: number;
  order?: string;
  timeZoneOffset?: string;
  assignee?: string;
  status?: string;
  recency?: string;
}

/** Defines the properties and functions bound by the component */
export interface PaginationBindings {
  isDisabled: boolean;
  pageSize: number;
  totalItemCount: number;
  selectedPageIndex: number;
  /** List of available page sizes */
  pageSizeList: number[];
  applyPageIndex: (pageIndex: number) => boolean;
  applyPageSize: (size: number) => void;
}

/** Interface for an info graphic */
export interface IInfoGraphic {
  /** The id for the infographic */
  id: string;
  /** The caption text */
  caption: string;
  /** The number of tickets */
  count?: number;
  /** Whether the info graphic is selected */
  selected?: boolean;
  /** Specify the chart slice color */
  color?: string;
  /** holds public id  */
  assigneePublicId?: string;
  /** holds filter caption  */
  filterCaption?: string;
}

export interface IAssignee {
  name: string;
  tickets: number;
  hide: boolean;
  id?: string;
}

interface ITimeoutSchedule {
  promise: Promise<any>;
}

interface IInfographicList {
  [id: string]: IInfoGraphic;
}

@Injectable()
export class DashboardPageService {
  /** Whether to hide or show charts */
  isChartsVisible =
    this.authorizationService.isSystemAdmin() || this.authorizationService.isAnyGroupAdminOrTechnician();

  /** List of tickets returned from server. */
  tickets: Ticket[];

  /** Indicates is the max tag title width has been calculated */
  computeMaxTagWidth = true;

  /** List of infographics */
  infoGraphics: IInfographicList;

  /** List of available ticket list filters */
  filterList: IInfoGraphicFilterList;

  filters: hd.IInfoGraphicListItem[] = [];
  /** List of available group filters */
  groupFilterList: IDropdownListItem[] = [];
  /** List of available group filters */
  groupFilterListObs: Subject<IDropdownListItem[]> = new ReplaySubject<IDropdownListItem[]>(1);
  /** The currently applied ticket filter */
  currentFilter = 'unresolved';

  /** The currently applied group filter */
  currentGroupId: number = null;
  currentGroupObs: Subject<string> = new ReplaySubject<string>(1);

  /** The currently applied infographic */
  currentInfographicId = '*';

  /** The current order */
  currentOrder: hd.ISortOrder;

  /** The current search text */
  searchText: string;
  searchTextObs: Subject<string> = new BehaviorSubject<string>(null);

  /** String that is displayed as the search hint, based on filter selection */
  searchHintText: string;

  /** The array of current assignees */
  assignees: IAssignee[] = [];

  chartDataSource: hd.IHiChartModel[];

  assigneePublicId: string = null;

  tagGroups: hd.ITagGroupModel[];

  tagGroupsDictionary: { [key: number]: hd.ITagGroupModel } = {};

  platformStatistics: hd.ISearchStatistic[];

  /** Version prefix to display */
  versionPrefix = '';

  /** Calculate the tag title width in pixels */
  tagTitleMaxWidth: { width: number };

  /** Whether to hide or show Infographics bar */
  isInfographicsCollapsed = false;

  /** Holds the value for the selected slice on the chart */
  selectedItem: IInfoGraphic;

  /** Data source for incidents chart */
  incidentChartData: hd.IHiChartModel[];

  /** Data source for resolved incidents chart */
  resolvedChartData: hd.IHiChartModel[];

  /** Data source for device platform chart */
  platformChartData: hd.IHiChartModel[];

  /** Data source for assignees chart */
  assigneeChartData: hd.IHiChartModel[];

  /** Provides the name of currently applied filter */
  filterText: string;

  /** Array contains references to all of the resolved related infographic objects */
  resolvedInfographics: IInfoGraphic[] = [];

  /** Pagination component data and links */
  pagination: PaginationBindings;

  /** Array contains references to all of the SLA-related infographic objects */
  slaInfographics: IInfoGraphic[] = [];

  /** Array contains references to all of the Status-related infographic objects */
  statusInfographics: IInfoGraphic[] = [];

  assigneeInfographics: IInfoGraphic[] = [];

  /** Ticket List defaults to loading initially */
  _isLoading = true;

  /** Info graphics values are not loaded by default */
  _areInfoGraphicsLoaded = false;

  /** should charts toggle button available */
  isChartToggleAvailable = true;

  activePromise: Promise<void>;

  ticketResourcePromise: Promise<hd.ISearchResult<hd.IExtendedTicketModel>>;

  defaultOrder: hd.ISortOrder = { property: 'creationDate', direction: hd.OrderDirection.Descending };

  orderDictionary: { [property: string]: hd.OrderDirection } = {};

  pageSizeKey = 'pageSize';

  ALL_GROUPS = 'allGroups';

  // in seconds. Include certificate expiry error
  DURATION_CERTIFICATE_ERROR = 15;

  groupList: IGroupModel[] = [];

  versionInfo = {
    versionPrefix: '5',
  };

  constructor(
    public userContextService: UserContextService,
    public ticketService: TicketService,
    private appSettingsService: AppSettingsService,
    public searchService: TicketSearchService,
    public authenticationService: AuthenticationService,
    public authorizationService: AuthorizationService,
    public pageTitleService: PageTitleService,
    public errorHandlerService: ErrorHandlerService,
    public featureToggleService: FeatureToggleService,
    public deviceService: XDeviceService,
    // public cssDefinitionsService: CssDefinitionsService,
    // public versionInfo,
    // public localStorageService: ng.local.storage.ILocalStorageService,
    public userSettingsService: UserSettingsService,
    public certificateService: CertificateService,
    public groupContextService: GroupContextService,
    public $uiRouter: ActivatedRoute,
    public xsI18nPipe: XsI18nPipe,
    public router: Router
  ) {
    this.initializeFilters();
  }

  initializeFilters = () => {
    // Admins and technicians can toggle the infographics
    if (this.authorizationService.isSystemAdmin() || this.authorizationService.isAnyGroupAdminOrTechnician()) {
      this.userSettingsService.getDashboardConfig().subscribe({
        next: (result: IDashboardConfig) => {
          this.isInfographicsCollapsed = !!result?.IsCollapsed;
          this.isChartsVisible = !result?.IsCollapsed;
        },
        error: () => {
          this.isInfographicsCollapsed = false;
        },
      });
    } else {
      // Infographics are always collapsed for Reporters
      this.isInfographicsCollapsed = true;
      this.isChartToggleAvailable = false;
    }

    // Define the built-in list of selectable filters
    this.filterList = {} as IInfoGraphicFilterList;

    // Only show the Mine and Unassigned filters for Admins & Technicians
    if (this.authorizationService.isSystemAdmin() || this.authorizationService.isAnyGroupAdminOrTechnician()) {
      this.filterList.mine = {
        id: 'mine',
        caption: 'ticketList_Filter_MyIncidents_Caption',
        badge: 0,
        statusInfographics: ['open', 'pending'],
        slaInfographics: ['duetoday', 'overdue'],
        resolvedInfographics: [],
        assigneeInfographics: ['assignee'],
      };

      this.filterList.unassigned = {
        id: 'unassigned',
        caption: 'ticketList_Filter_UnassignedIncidents_Caption',
        badge: 0,
        statusInfographics: ['open', 'pending'],
        slaInfographics: ['duetoday', 'overdue'],
        resolvedInfographics: [],
        assigneeInfographics: ['assignee'],
      };
    } else {
      this.currentFilter = 'all';
      this.defaultOrder = { property: 'updateDate', direction: hd.OrderDirection.Descending };
    }

    this.filterList.unresolved = {
      id: 'unresolved',
      caption: 'ticketList_Infographic_Unresolved_Caption',
      badge: 0,
      statusInfographics: ['open', 'pending'],
      slaInfographics: ['duetoday', 'overdue'],
      resolvedInfographics: [],
      assigneeInfographics: ['assignee'],
    };

    this.filterList.resolved = {
      id: 'resolved',
      caption: 'ticketList_Infographic_Resolved_Caption',
      badge: 0,
      statusInfographics: [],
      slaInfographics: [],
      resolvedInfographics: ['today', 'this_week', 'this_month', 'last_month', 'this_year', 'last_year'],
      assigneeInfographics: ['assignee'],
    };

    this.filterList.all = {
      id: 'all',
      caption: 'ticketList_Filter_AllIncidents_Caption',
      badge: 0,
      statusInfographics: ['open', 'pending'],
      slaInfographics: ['duetoday', 'overdue'],
      resolvedInfographics: [],
      assigneeInfographics: ['assignee'],
    };

    this.filters = Object.values(this.filterList);

    this.versionPrefix = this.versionInfo.versionPrefix;

    if (this.versionPrefix.charAt(this.versionPrefix.length - 1) === '.') {
      this.versionPrefix = this.versionPrefix.substr(0, this.versionPrefix.length - 1);
    }

    // Define the infographics
    this.setAssigneePublicId(this.$uiRouter.snapshot.queryParamMap.get('assigneePublicId'));

    this.infoGraphics = {
      // A placeholder for a hidden state.
      '*': {
        id: '*',
        caption: '',
      },
      open: {
        id: 'open',
        caption: 'ticketList_Infographic_Open_Caption',
        color: '#67708A',
        filterCaption: `${this.xsI18nPipe.transform('dashboard_chart_incidents_header')}: ${this.xsI18nPipe.transform(
          'ticketList_Infographic_Open_Caption'
        )}`,
      },
      pending: {
        id: 'pending',
        caption: 'ticketList_Infographic_Pending_Caption',
        color: '#FFD818',
        filterCaption: `${this.xsI18nPipe.transform('dashboard_chart_incidents_header')}: ${this.xsI18nPipe.transform(
          'ticketList_Infographic_Pending_Caption'
        )}`,
      },
      duetoday: {
        id: 'duetoday',
        caption: 'ticketList_Infographic_DueToday_Caption',
        color: '#FFA726',
        filterCaption: `${this.xsI18nPipe.transform('dashboard_chart_incidents_header')}: ${this.xsI18nPipe.transform(
          'ticketList_Infographic_DueToday_Caption'
        )}`,
      },
      overdue: {
        id: 'overdue',
        caption: 'ticketList_Infographic_OverDue_Caption',
        color: '#EB6251',
        filterCaption: `${this.xsI18nPipe.transform('dashboard_chart_incidents_header')}: ${this.xsI18nPipe.transform(
          'ticketList_Infographic_OverDue_Caption'
        )}`,
      },
      today: {
        id: 'today',
        caption: 'ticketList_Infographic_Resolved_Today',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_Today')}`,
      },
      this_week: {
        id: 'this_week',
        caption: 'ticketList_Infographic_Resolved_This_Week',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_This_Week')}`,
      },
      this_month: {
        id: 'this_month',
        caption: 'ticketList_Infographic_Resolved_This_Month',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_This_Month')}`,
      },
      last_month: {
        id: 'last_month',
        caption: 'ticketList_Infographic_Resolved_Last_Month',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_Last_Month')}`,
      },
      this_year: {
        id: 'this_year',
        caption: 'ticketList_Infographic_Resolved_This_Year',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_This_Year')}`,
      },
      last_year: {
        id: 'last_year',
        caption: 'ticketList_Infographic_Resolved_Last_Year',
        filterCaption: `${this.xsI18nPipe.transform(
          'dashboard_chart_resolved_incidents_header'
        )}: ${this.xsI18nPipe.transform('ticketList_Infographic_Resolved_Last_Year')}`,
      },
      assignee: {
        id: 'assignee',
        caption: 'Assignee',
        assigneePublicId: this.assigneePublicId,
      },
    } as { [key: string]: IInfoGraphic };
  };

  refreshGroupList() {
    this.groupContextService.refreshGroups().subscribe(groupList => {
      // refresh groups instead of using what has been cached.
      this.groupContextService.getAllowedGroups(true, groupList).then(
        result => {
          this.groupList = result;
          this.initCurrentGroupId(this.groupList);
          this.setGroupFilterList(this.groupList);
        },
        error => {
          console.error('Failed to fetch group list');
          console.error(error);
        }
      );
    });
  }

  navigateToDashboard() {
    this.router.navigate(['dashboard'], {
      queryParams: {
        filter: this.currentFilter || null,
        group: this.currentGroup || null,
        page: null,
        infographic: null,
        assigneePublicId: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  checkCertificateExpire = () => {
    this.certificateService.checkCertificateExpire().then((error: IAgentCertificateErrorExpire) => {
      if (error) {
        const errorMessage = this.xsI18nPipe.transform(error.message, error.expireDate);

        this.errorHandlerService.setError({ error_description: errorMessage } as hd.IError);

        setTimeout(() => {
          this.errorHandlerService.clearError();
        }, this.DURATION_CERTIFICATE_ERROR * 1000);
      }
    });
  };

  checkCertificateError = () => {
    this.certificateService.checkCertificateError().then((error: IAgentCertificateError) => {
      if (error) {
        const errorMessage = this.xsI18nPipe.transform(error.message);
        this.errorHandlerService.setError({ error_description: errorMessage } as hd.IError);
        setTimeout(() => {
          this.errorHandlerService.clearError();
        }, this.DURATION_CERTIFICATE_ERROR * 1000);
      }
    });
  };

  /** Determines if the ticket and related information is currently being loaded */
  isLoading = (): boolean => this._isLoading;

  /** Determines if the info graphics are currently being loaded */
  areInfoGraphicsLoaded = (): boolean =>
    this._areInfoGraphicsLoaded &&
    (this.slaInfographics.length > 0 || this.statusInfographics.length > 0 || this.resolvedInfographics.length > 0);

  areChartsLoaded = (): boolean =>
    // because our graphs are static placeholders, for now we just check if info graphics are loaded instead
    this.areInfoGraphicsLoaded();

  shouldShowGroupFilter = (): boolean => this.featureToggleService.isEnabled('Groups') && this.groupList.length > 1;

  /**
   * Applies the specified ticket list filter.
   *
   * @param filterId ID for the filter to be applied.
   * @param groupId ID for the filter to be applied.
   */
  applyFilter = (filterId: string) => {
    this.errorHandlerService.clearError();

    this.router.navigate(['dashboard'], {
      queryParams: {
        filter: filterId,
        group: this.currentGroup || null,
        page: null,
        infographic: null,
        assigneePublicId: null,
      },
      queryParamsHandling: 'merge',
    });
  };

  applyGroup = (groupId: string = null) => {
    this.errorHandlerService.clearError();
    groupId = groupId === this.ALL_GROUPS ? null : groupId; // when user selects "All groups"
    this.currentGroupId = groupId === null ? null : Number(groupId);
    this.router.navigate(['dashboard'], {
      queryParams: {
        filter: this.currentFilter,
        group: groupId,
        page: null,
        infographic: null,
        assigneePublicId: null,
      },
      queryParamsHandling: 'merge',
    });
  };

  /**
   * Apply filter based on assignee selection (from User Info Graphic)
   *
   * @param assigneePublicId the ID of the assignee to filter on
   */
  applyAssigneeFilter = (assigneePublicId?: string) => {
    this.applyInfoGraphic(this.infoGraphics.assignee, true, assigneePublicId);
  };

  /**
   * Applies the specified ticket list page size.
   *
   * @param size the selected page size to be applied.
   */
  applyPageSize = (size: number) => {
    this.errorHandlerService.clearError();
    this.setCurrentPageSize(size);

    const pageIndex = this.getCurrentPageIndex();
    // Set the router state params
    const stateParams: any = {
      size: this.getPageSize(),
      page: pageIndex > 1 ? pageIndex : null,
    };
    // Transition to the state without reloading the page
    this.router.navigate(['dashboard'], { queryParams: stateParams, queryParamsHandling: 'merge' });
  };

  /**
   * Applies the specified page index to visit.
   * If the value is invalid or larger than max possible value we ignore the request
   *
   * @param pageIndex the page index to be applied.
   */
  applyPageIndex = (pageIndex: number): boolean => {
    // Let's make sure we are not updating to the same page index
    const newUpdate = pageIndex !== this.getCurrentPageIndex();
    if (pageIndex !== undefined && pageIndex !== null && newUpdate && this.isValidPageNumber(pageIndex)) {
      this.errorHandlerService.clearError();
      const stateParams: any = { page: pageIndex > 1 ? pageIndex : null };
      this.router.navigate(['dashboard'], { queryParams: stateParams, queryParamsHandling: 'merge' });
      return true;
    }
    return false;
  };

  /**
   * Toggle the visibility of charts bar and persist its state to the backend
   */
  toggleChartsPanel = (doHide: boolean) => {
    this.isChartsVisible = !doHide;

    /* Persist flag into backend */
    const param: IDashboardConfig = {
      IsCollapsed: !this.isChartsVisible,
    };

    this.userSettingsService.updateDashboardConfig(param).subscribe({
      next: (result: IDashboardConfig) => {
        this.isInfographicsCollapsed = result?.IsCollapsed || false;
      },
      error: () => {
        this.isInfographicsCollapsed = false;
      },
    });

    // XTODO:
    // this.userSettingsService.updateDashboardConfig(param)
    //     .then((result: IDashboardConfig) => {
    //         if (this.featureToggleService.isEnabled('DeveloperMode')) {
    //             console.debug(`Success: persisted isInfographicsCollapsed as ${result.isCollapsed}`);
    //         }
    //     }).catch((error: any) => {
    //         /* log the error */
    //         console.debug('Failed to persist InfoGraphics visibility flag');
    //         console.error(error);
    //     });
  };

  /**
   * Clear currently applied filter
   */
  clearCurrentFilter = () => {
    this.applyInfoGraphic(this.selectedItem);
  };

  /**
   * Feature toggle implementation for Inforgraphics bar visibility toggle
   */
  isInfographicsToggleVisible = (): boolean =>
    !this.featureToggleService.isEnabled('Groups') ||
    this.authorizationService.isSystemAdmin() ||
    this.authorizationService.isAnyGroupAdminOrTechnician();

  /**
   * Applies the specified infographic filter
   *
   * @param infoGraphic The infographic to be applied
   */
  applyInfoGraphic = (infoGraphic: IInfoGraphic, apply: boolean = false, assigneePublicId: string = null): void => {
    this.errorHandlerService.clearError();

    // Clear existing info graphic selection
    this.infoGraphics[this.currentInfographicId].selected = false;
    this.setSelectedItem(this.infoGraphics[this.currentInfographicId]);

    const same = infoGraphic.id === this.currentInfographicId && (!apply || assigneePublicId === this.assigneePublicId);
    this.assigneePublicId = assigneePublicId;

    let queryParams: any;
    if (same) {
      queryParams = { infographic: '*', page: null, assigneePublicId: null };
      this.currentInfographicId = '*';
    } else {
      queryParams = { infographic: infoGraphic.id, page: null, assigneePublicId };
      this.currentInfographicId = infoGraphic.id;
    }

    // Apply selected info graphic
    this.infoGraphics[this.currentInfographicId].selected = true;
    this.setSelectedItem(this.infoGraphics[this.currentInfographicId]);

    /** Set assignee data model */
    this.setAssigneeChartDataModel();

    /** Set incidents data model */
    this.setIncidentChartDataModel();

    /** Set resolved data model */
    this.setResolvedChartDataModel();

    // Transition to the state without reloading the page
    this.router.navigate(['dashboard'], { queryParams, queryParamsHandling: 'merge' });
  };

  /** Initiates ticket search */
  search = (searchText: string): void => {
    this.errorHandlerService.clearError();
    const queryParams = { searchText, page: null };
    this.pagination.selectedPageIndex = 1;
    this.searchTextObs.next(searchText);
    this.searchText = searchText;
    this.setSearchTextAsTag();
    this.router.navigate(['dashboard'], { queryParams, queryParamsHandling: 'merge' });
  };

  /** Sets the current sort order */
  applyOrder = (order: hd.ISortOrder): void => {
    this.errorHandlerService.clearError();

    /** If no order specified, use the default order */
    if (!order) {
      order = this.defaultOrder;
    }

    // update the current order
    if (this.currentOrder && this.currentOrder.property in this.orderDictionary) {
      this.orderDictionary[this.currentOrder.property] = null;
    }
    this.currentOrder = order;
    this.orderDictionary[order.property] = order.direction;

    // reset skip value to its initial state.
    this.pagination.selectedPageIndex = 1;

    // Set the router state params
    const stateParams: any = {
      page: null,
      order: this.getOrder(),
    };
    // Transition to the state without reloading the page
    this.router.navigate(['dashboard'], { queryParams: stateParams, queryParamsHandling: 'merge' });
  };

  /** Downloads all pages of the ticket list as a csv file */
  downloadCsv = () => {
    // Create query params to retrieve all tickets given current filter/searchText/infographic
    const queryParams = this.getTicketsQueryParams();
    queryParams.skip = null;
    queryParams.take = '0';

    // Set time zone offset so CSV files contains dates in local time zone
    queryParams.timeZoneOffset = moment().utcOffset().toString();

    // Tokens to replace with translations
    const csvTokenPrefix = 'ticketList_Csv';
    const csvTokens = [
      'Header_TicketId',
      'Header_Subject',
      'Header_Status',
      'Status_1',
      'Status_2',
      'Status_3',
      'Status_4',
      'Header_Group',
      'Header_Priority',
      'Priority_1',
      'Priority_2',
      'Priority_3',
      'Priority_4',
      'Priority_5',
      'Header_Reporter',
      'Header_Assignee',
      'Header_Created',
      'Header_Updated',
      'Header_Due',
      'Header_Resolved',
      'Header_UserResolved',
      'Header_TimeTaken',
      'Header_SLAStatus',
      'Header_Predefined',
      'Header_UserDefined',
    ];

    // Get the CSV data and trigger download as incidents.csv
    this.searchService.getCsv(queryParams).subscribe(
      (csv: string) => {
        // Replace all remaining tokens from CSV Tokens list
        for (const tokenType of csvTokens) {
          const token = csvTokenPrefix + tokenType;
          while (csv.indexOf(token) > -1) {
            csv = csv.replace(token, this.xsI18nPipe.transform(token));
          }
        }

        csv = csv.replace('N/A', this.xsI18nPipe.transform('N/A'));

        // Add BOM to file - fixes Excel issue with reading CSV in UTF-8
        csv = '\ufeff' + csv;
        const zip: JSZIP = new JSZIP();
        let filename = this.xsI18nPipe.transform('dashboard_export_csv_filename_header_Incidents') + moment(new Date()).format('YYYY-MM-DDTHHmmss');
        try {
          zip.file(filename + '.csv', csv);
          filename = filename.replace(/:\s*/g, '');
          zip.generateAsync({ type: 'blob' }).then(function (content) {
            FileSaver.saveAs(content, filename + '.zip');
          });
        } catch (e) {
          alert(this.xsI18nPipe.transform(e));
        }
      },
      (error: any) => {
        console.error('Failed to generate CSV file');
        console.error(error);
        this.setError(error);
      }
    );
  };

  /** Gets the start of the viewed range of tickets */
  private getPageRangeStart = (): number => this.calculateSkipValue() + 1;

  /** Gets the end of the viewed range of tickets */
  private getPageRangeEnd = (): number => {
    let currentItemsInPage: number = this.getPageSize();
    if (this.getCurrentPageIndex() === this.getMaxPageValue()) {
      const remainder = this.getTicketCount() % this.getPageSize();
      currentItemsInPage = remainder > 0 ? remainder : currentItemsInPage;
    }
    return this.getPageRangeStart() + currentItemsInPage - 1;
  };

  /** Gets max page index value */
  private getMaxPageValue = (): number => Math.ceil(this.getTicketCount() / this.getPageSize());

  /** Gets selected page index in pagination component */
  getCurrentPageIndex = (): number => (this.pagination ? this.pagination.selectedPageIndex : 0);

  /** Checks if we want to show search summary on top of the ticket list */
  private showSearchSummary = (): boolean => this.getTicketCount() > 0;

  get incidentsTitle(): string {
    let content: string;
    if (this.showSearchSummary) {
      content = this.xsI18nPipe.transform(
        'ticketList_page_summary',
        this.getPageRangeStart(),
        this.getPageRangeEnd(),
        this.getTicketCount()
      );
    } else {
      content = this.xsI18nPipe.transform('ticketList_pageSummaryEmpty_Caption');
    }
    return `${content} ${this.xsI18nPipe.transform('ticketList_page_incidents')}`;
  }

  /** Sets total count of available tickets */
  setTotalTicketCount = (totalTicketsCount: number) => {
    this.pagination.totalItemCount = totalTicketsCount;
  };

  /**
   * Updates current skip value based on a valid page index.
   * If the value is not valid we will reset skip to its initial state.
   * If the value is valid but larger than available range we will set it to largest possible value.
   *
   * @param pageIndex the page index to be applied.
   */
  setPageIndex = (pageIndex: number): void => {
    if (!isNaN(pageIndex) && !isNaN(this.getPageSize()) && pageIndex > 0) {
      if (!this.isValidPageNumber(pageIndex)) {
        // User value is not valid let's put the user at the last page.
        this.pagination.selectedPageIndex = this.getMaxPageValue();
      } else {
        this.pagination.selectedPageIndex = pageIndex;
      }
    } else {
      this.pagination.selectedPageIndex = 1;
    }
  };

  get currentGroup() {
    return this.currentGroupId === null ? this.ALL_GROUPS : this.currentGroupId.toString();
  }

  private getAssigneePublicId() {
    if (this.currentInfographicId === 'assignee') {
      return this.assigneePublicId;
    } else {
      return undefined;
    }
  }

  private setAssigneePublicId(assigneePublicIdStr: string) {
    if (assigneePublicIdStr) {
      this.assigneePublicId = assigneePublicIdStr;
    } else {
      this.assigneePublicId = null;
    }
  }

  private initCurrentGroupId(groups: IGroupModel[]) {
    if (!groups || groups.length === 0) {
      this.currentGroupId = null;
      this.currentGroupObs.next(this.ALL_GROUPS);
    }
  }

  setGroupFilterList(groups: IGroupModel[]) {
    const buildGroupFilterList = [];
    if (!groups || groups.length === 0) {
      this.groupFilterListObs.next(null);
      return;
    }

    // set "all" option for group filter
    buildGroupFilterList.push({
      id: this.ALL_GROUPS,
      caption: this.xsI18nPipe.transform('ticketList_groupFilter_AllGroups'),
    });

    for (const group of groups) {
      buildGroupFilterList.push({ id: group.groupId.toString(), caption: group.groupName });
    }
    this.groupFilterListObs.next(buildGroupFilterList);
  }

  getCurrentGroupName() {
    if (!this.groupList || this.groupList.length === 0) {
      return '';
    }

    for (const group of this.groupList) {
      if (group.groupId === this.currentGroupId) {
        return group.groupName;
      }
    }

    return '';
  }

  /**
   * Updates current page size value based on a valid page size value.
   *
   * @param size the selected page size to be applied.
   */
  setCurrentPageSize = (size: number) => {
    if (this.pagination.pageSizeList.includes(size)) {
      const oldPageSize = this.pagination.pageSize;
      this.setPageIndexWithPageSize(oldPageSize, size);
      this.pagination.pageSize = size;
      // this.localStorageService.set(this.pageSizeKey, size);
    }
  };

  /**
   * Updates current page index based on the page size change.
   *
   * @param oldPageSize the old page size value.
   * @param newPageSize the new page size value.
   * @return true if the page index was updated, false otherwise.
   */
  private setPageIndexWithPageSize = (oldPageSize: number, newPageSize: number) => {
    const oldSkip: number = this.calculateSkipValue(oldPageSize, this.pagination.selectedPageIndex);
    if (oldSkip > 0) {
      const newSkip: number = Math.floor(oldSkip / newPageSize) * newPageSize;
      this.pagination.selectedPageIndex = this.calculatePageIndexFromSkip(newSkip, newPageSize);
      return true;
    }
    return false;
  };

  /** Gets current page size */
  getPageSize = (): number => (this.pagination ? this.pagination.pageSize : 0);

  /**
   * Calcuates the count of skiped items.
   * This value is used to retrive the current page list by the server.
   */
  private calculateSkipValue = (pageSize?: number, pageIndex?: number): number => {
    if (pageSize === undefined || pageSize === null) {
      pageSize = this.pagination ? this.pagination.pageSize : 0;
    }
    if (pageIndex === undefined || pageIndex === null) {
      pageIndex = this.pagination ? this.pagination.selectedPageIndex : 0;
    }
    // skip is calculated based on index page 0
    return pageSize * (pageIndex - 1);
  };

  /** Gets total number of available pages */
  getTicketCount = (): number => (this.pagination ? this.pagination.totalItemCount : 0);

  private isValidPageNumber = (pageIndex: number) => pageIndex >= 0 && pageIndex <= this.getMaxPageValue();
  // return isValidPageNumber(pageIndex, this.getMaxPageValue());

  /** Initialies pagination component */
  initPagination = () => {
    // const pageSize = Number(this.localStorageService.get<number>(this.pageSizeKey)) || 10;
    const pageSize = 10;
    this.pagination = {
      isDisabled: false,
      pageSize,
      totalItemCount: 1,
      selectedPageIndex: 1,
      // Define the built-in list of selectable page sizes
      pageSizeList: [10, 25, 50, 100],
      applyPageIndex: this.applyPageIndex,
      applyPageSize: this.applyPageSize,
    };
  };

  /** Calculates the current page index based on current skip and page size values. */
  private calculatePageIndexFromSkip = (skip?: number, pageSize?: number): number => {
    if (skip === undefined || skip === null) {
      skip = this.calculateSkipValue();
    }
    if (pageSize === undefined || pageSize === null) {
      pageSize = this.getPageSize();
    }
    return Math.floor(skip / pageSize) + 1;
  };

  /** Checks if we are not in the same state once a request is fulfiled. */
  private isValidRequest = (promise: Promise<any>, activePromise: Promise<any>) =>
    // XTODO
    // if (this.$uiRouter.current.name !== 'dashboard') {
    //     return false;
    // }
    // if (activePromise !== promise) {
    //     return false;
    // }
    true;

  /** Sets the error */
  private setError = (err: IError): void => {
    this.errorHandlerService.setError(err);
  };

  /** Gets a list of paramters to use by backend query requests. */
  private getTicketsQueryParams = (
    // infographicId?: string,
    take?: string,
    skip?: string,
    order?: string
  ): TicketParams => {
    const params: TicketParams = {};
    if (this.searchText !== undefined && this.searchText !== null && this.searchText !== '') {
      params.searchText = this.searchText;
    }

    // if (!infographicId) {
    // infographicId = this.currentInfographicId;
    // }

    params.filter = this.currentFilter;

    // Update take parameter to limit page size.
    if (!take) {
      take = this.getPageSize().toString();
    }
    params.take = take;

    if (!skip) {
      skip = this.calculateSkipValue().toString();
    }

    params.skip = skip;

    if (!order) {
      order = this.getOrder();
    }
    params.order = order;

    params.group = Number.isInteger(this.currentGroupId) ? this.currentGroupId : null;

    params.assignee = this.getAssigneePublicId();

    if (['duetoday', 'overdue', 'open', 'pending'].includes(this.currentInfographicId)) {
      params.status = this.currentInfographicId;
    }

    if (
      ['today', 'this_week', 'this_month', 'last_month', 'this_year', 'last_year'].includes(this.currentInfographicId)
    ) {
      params.recency = this.currentInfographicId;
    }

    return params;
  };

  /** Retrieves ticket list based on the provided paramters */
  private setTickets = (tickets: hd.IExtendedTicketModel[], total: number): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const loadedTickets: Ticket[] = [];
      // Create a list of unique user info IDs to query (for reporter and assignee)
      const userInfoIds: string[] = [];
      const timeoutSchedule: ITimeoutSchedule = { promise: null } as ITimeoutSchedule;
      tickets.forEach(ticket => {
        const newTicket = new Ticket(
          ticket,
          this.ticketService,
          timeoutSchedule,
          this.groupContextService,
          this.xsI18nPipe,
          this.featureToggleService
        );
        loadedTickets.push(newTicket);

        // Check which userInfo or tags need to be loaded
        if (!ticket.reporterName && !!ticket.reporterPublicId && userInfoIds.indexOf(ticket.reporterPublicId) === -1) {
          userInfoIds.push(ticket.reporterPublicId);
        }

        if (!ticket.assignee && !!ticket.assigneePublicId && userInfoIds.indexOf(ticket.assigneePublicId) === -1) {
          userInfoIds.push(ticket.assigneePublicId);
        }

        if (ticket.hasOwnProperty('tags')) {
          if (ticket['tags'].length > 0) {
            newTicket.associatedTags = ticket['tags'];
          }
        }
      });

      this.userContextService.getUserInfoList(userInfoIds).subscribe({
        next: users => {
          if (users) {
            // Populate user info dictionary
            const userInfoDictionary: { [userInfoId: number]: hd.IUserInfoModel } = {};

            users.forEach(userInfo => {
              userInfoDictionary[userInfo.userInfoPublicId] = userInfo;
            });

            // Populate missing fields (reporter, assignee)
            loadedTickets.forEach(ticket => {
              if (!ticket.reporterName) {
                const reporterPublicId = ticket.reporterPublicId;
                const reporter = userInfoDictionary[reporterPublicId];
                if (reporter) {
                  ticket.reporterName = reporter.displayName;
                }
              }

              if (!ticket.assignee) {
                const assigneePublicId = ticket.assigneePublicId;
                const assignee = userInfoDictionary[assigneePublicId];
                if (assignee) {
                  ticket.assignee = assignee.displayName;
                }
              }

              if (ticket.dueDate === '') {
                const dueDate = "N/A";
                ticket.dueDate = dueDate;
              }
            });
          }
          this.setTotalTicketCount(total);
          this.tickets = loadedTickets;

          resolve();
        },
        error: () => reject(),
      });
    });

  private loadTickets(params: TicketParams, charts: boolean, table: boolean): Promise<void> {
    this._isLoading = true;
    const chartIds: string[] = [];
    if (charts) {
      chartIds.push('dashAssignee', 'dashCount', 'dashPlatform');
    }
    if (table) {
      chartIds.push('dashTable');
    }

    const take = params.take === undefined ? 10 : parseInt(params.take, 10);
    const skip = params.skip === undefined ? 0 : parseInt(params.skip, 10);
    const searchText = params.searchText ?? null;
    const sort = params.order ?? '';
    const filter = params.filter ?? 'all';

    // Todo: Memoize/Cache ticket data requests

    return this.searchService
      .getTicketData(
        chartIds,
        filter,
        searchText,
        sort,
        skip,
        take,
        params.group,
        params.assignee,
        params.status,
        params.recency,
        moment().utcOffset()
      )
      .toPromise()
      .then((searchResults: any[]) => {
        const results: { [key: string]: any } = {};
        searchResults.forEach(result => {
          if (chartIds.includes(result.chartId)) {
            results[result.chartId] = result.data;
          }
        });
        if (!!results.dashAssignee) {
          this.setTopAssignees(results.dashAssignee.results);
        }
        if (!!results.dashCount) {
          this.setFilterCountById(
            results.dashCount.results,
            [
              'duetoday',
              'overdue',
              'open',
              'pending',
              'today',
              'this_week',
              'this_month',
              'last_month',
              'this_year',
              'last_year',
            ],
            !!searchText
          );
        }
        if (!!results.dashPlatform) {
          this.setPlatformStatistics(results.dashPlatform.results);
        }

        if (!!results.dashTable) {
          return this.setTickets(results.dashTable.results, results.dashTable.total);
        }
      });
  }

  loadStateUsingQueryParams(queryParams: ParamMap) {
    // Get the filter (if provided) from the state params and find the filter query
    let currentFilterName;

    // Parse Route to params
    const group: number = parseInt(queryParams.get('group'), 10);
    const filter = queryParams.get('filter');
    const size: string = queryParams.get('size');
    const page: string = queryParams.get('page');
    const searchText = queryParams.get('searchText');
    const sortOrder = queryParams.get('order');
    const infographic = (queryParams.get('infographic') || '*').toLowerCase();
    const assigneePublicId = queryParams.get('assigneePublicId');
    this.setAssigneePublicId(assigneePublicId);

    let filterQuery;
    if (filter !== undefined && filter !== null) {
      filterQuery = filter.toString().toLowerCase();
      if (this.filterList[filterQuery]) {
        this.currentFilter = filterQuery;
      } else {
        filterQuery = this.currentFilter;
      }
    } else {
      filterQuery = this.currentFilter;
    }
    currentFilterName = this.filterList[filterQuery].caption;

    // Update current page size  based on the provided size.
    if (size !== undefined && size !== null) {
      this.setCurrentPageSize(parseInt(size, 10));
    }
    if (page !== undefined && page !== null) {
      this.pagination.selectedPageIndex = parseInt(page, 10);
    } else {
      this.pagination.selectedPageIndex = 1;
    }

    // Get the search text from the state params
    this.searchText = searchText;

    // Get the order from the state params
    this.currentOrder = this.parseOrder(sortOrder);
    this.orderDictionary[this.currentOrder.property] = this.currentOrder.direction;

    this.filters = Object.values(this.filterList);

    // Split the infographics in groups so 'status' infographics and 'sla' infographics can each be displayed together
    this.statusInfographics = this.getInfoGraphics(this.filterList[filterQuery].statusInfographics);
    this.slaInfographics = this.getInfoGraphics(this.filterList[filterQuery].slaInfographics);
    this.resolvedInfographics = this.getInfoGraphics(this.filterList[filterQuery].resolvedInfographics);
    this.assigneeInfographics = this.getInfoGraphics(this.filterList[filterQuery].assigneeInfographics);

    // Set the page title based on the current filter applied
    if (this.searchText) {
      this.pageTitleService.setTitle(
        this.xsI18nPipe.transform('ticketList_PageTitle_ForSearch', currentFilterName, this.searchText)
      );
    } else {
      this.pageTitleService.setTitle(this.xsI18nPipe.transform('ticketList_PageTitle', currentFilterName));
    }

    if (infographic && infographic in this.infoGraphics) {
      this.currentInfographicId = infographic;
    } else {
      this.currentInfographicId = '*';
    }

    for (const [key, value] of Object.entries(this.infoGraphics)) {
      value.selected = key === this.currentInfographicId;
    }

    this.setSelectedItem(this.infoGraphics[this.currentInfographicId]);

    if (Number.isInteger(group)) {
      this.currentGroupId = group;
      this.currentGroupObs.next(group.toString());
    } else {
      this.currentGroupId = null;
      this.currentGroupObs.next(this.ALL_GROUPS);
    }
    this.refreshGroupList();

    // Load the tickets based on search, filter and infographic
    this.loadTickets(this.getTicketsQueryParams(), true, true)
      .then(() => {
        if (page !== undefined && page !== null) {
          // Confirm that the requested page falls within the range of total results
          const requestedPage = this.getCurrentPageIndex();
          this.setPageIndex(requestedPage);
          const safePage = this.getCurrentPageIndex();
          if (safePage < requestedPage) {
            // Reload with a safe page if the request was unsafe
            this.tickets = [];
            return this.applyPageIndex(safePage);
          }
        }
      })
      .then(() => {
        this._isLoading = false;
      });

    /** get the currently selected filter */
    this.getSelectedItem();

    /** Set filter text */
    this.setFilterText();
    this.setSearchHintText(this.currentFilter);

    this.checkCertificateExpire();
    this.checkCertificateError();
  }

  sortColumn = (order: hd.ISortOrder) => {
    this.applyOrder(order);
  };

  /**
   * Sets the top assignees
   *
   * @param queryResult the result of the assignee query
   */
  private setTopAssignees = (queryResult: any) => {
    this.assignees = [];

    if (queryResult && queryResult.length && queryResult.length > 0) {
      queryResult.forEach(result => {
        this.assignees.push({
          id: result.key,
          name: this.xsI18nPipe.transform(result.name),
          tickets: result.value,
          hide: false,
        });
      });
    }

    /** set data model for assignee chart */
    this.setAssigneeChartDataModel();
    this.setFilterText();
  };

  private setPlatformStatistics(results: hd.ISearchStatistic[]) {
    // Set additional derived properties on the results
    this.platformStatistics = results.map((statistic: hd.ISearchStatistic) => {
      const cssClass = this.deviceService.getPlatformCssClass(statistic.key);
      // XTODO:
      // Finally, set the content of the class (the icon) as the label for the statistic statistic.
      return {
        ...statistic,
        // $label: this.cssDefinitionsService.getDeviceIcon('.' + cssClass + '::before');
        $label: 'stat label',
        $labelCss: 'hd-icon ' + cssClass,
        $barCss: cssClass,
      };
    });

    this.setPlatformChartDataModel();
  }

  private setFilterCountById(counts: number[], countIds: string[], searchApplied: boolean) {
    if (counts.length !== countIds.length) {
      console.error(`Filter count result length does not match request ${counts.length}/${countIds.length}`);
      return;
    }

    countIds.forEach((countId, index) => {
      if (this.infoGraphics.hasOwnProperty(countId)) {
        const infoGraphic = this.infoGraphics[countId];
        infoGraphic.count = counts[index];
      } else {
        console.error('Failed to lookup count for infographic filter: ' + countId);
      }
    });
    if (!searchApplied) {
      // Update active filter count.
      this.filterList[this.currentFilter].badge = this.infoGraphics['*'].count;
    }
    this.setIncidentChartDataModel();
    this.setResolvedChartDataModel();
  }

  /** Gets list of applied infographics based on array indices */
  private getInfoGraphics = (selections: string[]) => selections.map(selection => this.infoGraphics[selection]);

  /** Parse a sort order string, returning default sort order if not specified or cannot be parsed */
  private parseOrder = (order: string): hd.ISortOrder => {
    let parsedOrder: hd.ISortOrder;
    if (!order || order.length < 2) {
      parsedOrder = this.defaultOrder;
    } else {
      let direction: hd.OrderDirection;
      let property: string;

      switch (order[0]) {
        case '+':
          direction = hd.OrderDirection.Ascending;
          property = order.substr(1);
          break;

        case '-':
          direction = hd.OrderDirection.Descending;
          property = order.substr(1);
          break;

        default:
          direction = hd.OrderDirection.Ascending;
          property = order;
          break;
      }

      parsedOrder = { property, direction };
    }

    return parsedOrder;
  };

  /** Returns the string representing the current sort order */
  private getOrder = (order?: hd.ISortOrder): string => {
    if (!order) {
      order = this.currentOrder;
    }

    let orderText: string = null;
    if (order) {
      orderText = (order.direction === hd.OrderDirection.Descending ? '-' : '') + order.property;
    }
    return orderText;
  };

  private setSearchHintText = (filter: string): void => {
    this.searchHintText = this.xsI18nPipe.transform('ticketList_SearchHint');
  };

  /** Format the search text as tag */
  private setSearchTextAsTag = () => {
    if (this.searchText && this.searchText.charAt(0) === '#') {
      // replace one or more # prefixes with #
      this.searchText = this.searchText.replace(/^#+/g, '#');

      // replace any spaces in the tag with underscores
      this.searchText = this.searchText.replace(/ +/g, '_');
    }
  };

  /** apply assignee filter based on the slice selected */
  applyAssigneeChartFilter = (assignee: IAssignee) => {
    this.applyAssigneeFilter(assignee.id);
  };

  /** set data model for incident chart */
  private setIncidentChartDataModel = () => {
    const element = [];
    this.slaInfographics
      .filter(sla => sla.count > 0)
      .forEach(sla => {
        element.push({
          name: sla.caption,
          y: sla.count,
          sliced: sla.selected,
          color: sla.color,
          model: sla,
        } as hd.IHiChartModel);
      });
    this.statusInfographics
      .filter(status => status.count > 0)
      .forEach(status => {
        element.push({
          name: status.caption,
          y: status.count,
          sliced: status.selected,
          color: status.color,
          model: status,
        } as hd.IHiChartModel);
      });
    this.incidentChartData = element;
  };

  /** set data model for resolved chart */
  private setResolvedChartDataModel = () => {
    const element = [];
    this.resolvedInfographics
      .filter(resolved => resolved.count > 0)
      .forEach(resolved => {
        element.push({
          name: resolved.caption,
          y: resolved.count,
          sliced: resolved.selected,
          color: resolved.color,
          model: resolved,
        } as hd.IHiChartModel);
      });
    this.resolvedChartData = element;
  };

  /** set data model for platform chart */
  private setPlatformChartDataModel = () => {
    const elements = [];
    this.platformStatistics
      .filter(platform => platform.value > 0)
      .forEach(platform => {
        const deviceKind = this.getDeviceKind(platform.key);
        const element = elements.find(p => p.name === deviceKind);
        if (element) {
          element.y += platform.value;
        } else {
          elements.push({
            name: deviceKind,
            y: platform.value,
            icon: platform.$labelCss,
            model: platform,
          } as hd.IHiChartModel);
        }
      });
    this.platformChartData = elements;
  };

  /** set data model for assignee chart */
  private setAssigneeChartDataModel = () => {
    this.selectedItem.assigneePublicId = this.getAssigneePublicId();
    this.assigneeChartData = this.assignees
      .filter(a => a.tickets > 0)
      .map<hd.IHiChartModel>(a => ({
        name: !a.name ? this.xsI18nPipe.transform('userInfoGraphic_unassigned_Caption') : a.name,
        y: a.tickets,
        sliced: this.isAssigneeSelected(a),
        model: a,
      }));

    /** sort data by descending order */
    this.assigneeChartData.sort((a, b) => b.y - a.y);
  };

  /** set the selected slice from chart */
  private setSelectedItem = (selected: IInfoGraphic) => {
    this.selectedItem = selected;
  };

  /** get selected assignee based on public id */
  private isAssigneeSelected = (assignee: IAssignee): boolean => {
    if (!this.selectedItem || this.selectedItem.id !== 'assignee') {
      return false;
    }
    return assignee.id === this.selectedItem.assigneePublicId;
  };

  /** get the selected slice from chart */
  private getSelectedItem = () => {
    for (const [_, value] of Object.entries(this.infoGraphics)) {
      if (value.selected) {
        this.selectedItem = value;
      }
    }
  };

  /** set current filter text  */
  private setFilterText = () => {
    if (this.selectedItem.id === 'assignee') {
      let filterValue = this.xsI18nPipe.transform('userInfoGraphic_unassigned_Caption');
      if (this.selectedItem.assigneePublicId) {
        const selectedAssignee = this.assignees.find(e => e.id && e.id === this.selectedItem.assigneePublicId);
        if (selectedAssignee) {
          filterValue = selectedAssignee.name;
        }
      }
      this.filterText = `${this.xsI18nPipe.transform('dashboard_chart_assignees_header')}: ${filterValue}`;
    } else {
      this.filterText = '';
      if (this.selectedItem && this.selectedItem.id !== '*') {
        this.filterText = this.selectedItem.filterCaption ? this.selectedItem.filterCaption : this.selectedItem.caption;
      }
    }
  };

  /** get device kind based on device family */
  private getDeviceKind = (deviceFamily: string): string => {
    switch (deviceFamily) {
      case 'Android':
      case 'AndroidPlus':
        return 'Android';
      case 'WindowsCE':
      case 'WindowsRuntime':
      case 'WindowsDesktop':
      case 'WindowsModernDesktop':
      case 'WindowsPhone':
        return 'Windows';
      case 'Apple':
      case 'AppleDesktop':
        return 'Apple';
      case 'Printer':
      case 'Scanner':
        return 'Printer';
      case 'Tizen':
      case 'Linux':
        return 'Linux';
      case 'Unmanaged':
        return this.xsI18nPipe.transform('associateDevices_Form_Unmanaged_Title');
      default:
        return this.xsI18nPipe.transform('associateDevices_Form_Unmanaged_Title');
    }
  };
}
